/**
 * ECOSMITH Scroll Video Component
 * Updated: Layered hex + curves SVG background, elegant editorial typography
 * Theme: Sage Green (#7A9E7E, #94B58A, #A8C5AA) — Ecosmith brand
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

class EcoScrollVideo extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.scrollTrigger = null;
    this.pinTrigger = null;
  }

  connectedCallback() {
    this.render();
    requestAnimationFrame(() => this.initScrollVideo());
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this.scrollTrigger) { this.scrollTrigger.kill(); this.scrollTrigger = null; }
    if (this.pinTrigger) { this.pinTrigger.kill(); this.pinTrigger = null; }
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        /* ── Google Fonts: editorial pair ─────────────────────────── */
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

        :host {
          display: block;
          position: relative;
          width: 100%;
          --green-dark:   #F5F9F5;
          --green-mid:    #F5F9F5;
          --green-sage:   #7A9E7E;
          --green-light:  #94B58A;
          --green-pale:   #A8C5AA;
          --white:        #172319;
          --white-dim:    rgba(23,35,25,0.85);
          --card-bg:      rgba(255,255,255,0.6);
          --card-border:  rgba(122,158,126,0.3);
          --font-display: 'Stopmotion', 'Space Age', system-ui, sans-serif;
          --font-body:    'Inter', system-ui, sans-serif;
        }

        /* ── Layout ──────────────────────────────────────────────── */
        .scroll-video-section {
          position: relative;
          width: 100%;
          height: 400vh;
        }

        .sticky-container {
          position: sticky;
          top: 0;
          height: 100vh;
          width: 100%;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--green-dark);
        }

        /* ── SVG background ──────────────────────────────────────── */
        .svg-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }

        /* Slow drift layers */
        .hex-layer-far   { animation: drift-far   40s ease-in-out infinite alternate; }
        .hex-layer-mid   { animation: drift-mid   28s ease-in-out infinite alternate; }
        .hex-layer-near  { animation: drift-near  18s ease-in-out infinite alternate; }
        .curves-layer    { animation: curves-sway 35s ease-in-out infinite alternate; }

        @keyframes drift-far  { from { transform: translate(0,0)     rotate(0deg); }
                                 to   { transform: translate(18px,12px) rotate(6deg); } }
        @keyframes drift-mid  { from { transform: translate(0,0)       rotate(0deg); }
                                 to   { transform: translate(-14px,20px) rotate(-5deg); } }
        @keyframes drift-near { from { transform: translate(0,0)    rotate(0deg); }
                                 to   { transform: translate(10px,-16px) rotate(8deg); } }
        @keyframes curves-sway { from { transform: translateX(0); }
                                  to   { transform: translateX(30px); } }

        /* Rotating gradient mesh — matches eco-product-story */
        .grad-mesh {
          position: absolute;
          inset: 0;
          width: 200%; height: 200%;
          top: -50%; left: -50%;
          background:
            radial-gradient(ellipse 55% 45% at 30% 35%, rgba(80,170,100,0.13) 0%, transparent 60%),
            radial-gradient(ellipse 50% 60% at 70% 65%, rgba(50,120,70,0.09) 0%, transparent 55%),
            radial-gradient(ellipse 70% 70% at 50% 50%, rgba(20,80,40,0.16) 0%, transparent 70%);
          animation: grad-rotate 30s ease-in-out infinite;
          pointer-events: none;
          z-index: 1;
        }

        @keyframes grad-rotate {
          0%,100% { transform: translate(0%,0%)   rotate(0deg);   }
          33%      { transform: translate(-3%,-3%) rotate(120deg); }
          66%      { transform: translate(3%,3%)   rotate(240deg); }
        }

        /* Vignette */
        .vignette {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 50% 50%, transparent 35%, rgba(245,249,245,0.7) 100%);
          z-index: 2;
          pointer-events: none;
        }

        /* ── Content ─────────────────────────────────────────────── */
        .content-layer {
          position: relative;
          z-index: 3;
          width: 100%;
          max-width: 1200px;
          padding: 2.5rem 2rem;
          margin: 0 auto;
        }

        /* ── Header ─────────────────────────────────────────────── */
        .section-header {
          text-align: center;
          margin-bottom: 2.75rem;
        }

        .section-label {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 7px 20px;
          background: rgba(122,158,126,0.18);
          border: 1px solid rgba(122,158,126,0.45);
          border-radius: 9999px;
          color: #3d6b42;
          font-family: var(--font-body);
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          margin-bottom: 1.25rem;
          backdrop-filter: blur(12px);
        }

        .label-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: var(--green-light);
          animation: pulse-dot 2.4s ease-in-out infinite;
        }

        @keyframes pulse-dot {
          0%,100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(0.7); }
        }

        /* Display headline — serif */
        .section-title {
          font-family: var(--font-display);
          font-size: clamp(2.6rem, 5.5vw, 4.4rem);
          font-weight: 800;
          color: var(--white);
          line-height: 1.08;
          letter-spacing: -0.02em;
          margin: 0 0 0.2em;
        }

        /* Accent word — color only (Orbitron has no true italic) */
        .section-title em {
          font-style: normal;
          color: var(--green-pale);
        }

        /* Body subtitle — sans */
        .section-subtitle {
          font-family: var(--font-body);
          font-size: 1.05rem;
          font-weight: 500;
          color: rgba(23,35,25,0.88);
          max-width: 540px;
          margin: 0 auto;
          line-height: 1.7;
          letter-spacing: 0.01em;
        }

        /* ── Process Steps ────────────────────────────────────────── */
        .progress-steps {
          display: flex;
          justify-content: center;
          gap: clamp(1rem, 3vw, 2.5rem);
          margin-top: 2.25rem;
          flex-wrap: wrap;
        }

        .step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.6rem;
          opacity: 0.35;
          transition: opacity 0.4s ease, transform 0.4s ease;
          cursor: pointer;
        }

        .step:hover { opacity: 0.65; transform: translateY(-4px); }
        .step.active { opacity: 1; }

        .step-number {
          width: 50px; height: 50px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.07);
          border: 1.5px solid rgba(255,255,255,0.18);
          border-radius: 50%;
          font-family: var(--font-display);
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--white);
          transition: all 0.4s cubic-bezier(0.4,0,0.2,1);
        }

        .step.active .step-number {
          background: var(--green-sage);
          border-color: var(--green-sage);
          box-shadow: 0 0 28px rgba(122,158,126,0.45);
          transform: scale(1.1);
        }

        .step-label {
          font-family: var(--font-body);
          font-size: 0.72rem;
          font-weight: 500;
          color: var(--white);
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        /* Connector line between steps */
        .step-connector {
          width: clamp(20px, 3vw, 40px);
          height: 1px;
          background: rgba(122,158,126,0.25);
          align-self: center;
          margin-bottom: 1.5rem;
          flex-shrink: 0;
        }

        /* ── Feature Cards ───────────────────────────────────────── */
        .feature-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
          margin-top: 2.5rem;
        }

        .feature-card {
          padding: 1.75rem 1.5rem;
          background: var(--card-bg);
          backdrop-filter: blur(18px);
          border: 1px solid var(--card-border);
          border-radius: 18px;
          opacity: 0;
          transform: translateY(28px);
          transition: border-color 0.35s ease, box-shadow 0.35s ease, transform 0.35s ease, background 0.35s ease;
          position: relative;
          overflow: hidden;
        }

        /* Subtle top highlight line on each card */
        .feature-card::before {
          content: '';
          position: absolute;
          top: 0; left: 15%; right: 15%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(148,181,138,0.5), transparent);
          opacity: 0;
          transition: opacity 0.35s ease;
        }

        .feature-card.visible { opacity: 1; transform: translateY(0); }

        .feature-card:hover {
          background: rgba(255,255,255,0.8);
          border-color: rgba(122,158,126,0.5);
          transform: translateY(-6px);
          box-shadow: 0 22px 44px rgba(0,0,0,0.08), 0 0 0 1px rgba(148,181,138,0.2);
        }

        .feature-card:hover::before { opacity: 1; }

        .feature-icon {
          width: 52px; height: 52px;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, rgba(122,158,126,0.22), rgba(122,158,126,0.08));
          border-radius: 14px;
          margin-bottom: 1.1rem;
          border: 1px solid rgba(122,158,126,0.22);
        }

        .feature-icon svg {
          width: 26px; height: 26px;
          color: var(--green-pale);
          stroke-width: 1.5;
        }

        /* Card title — serif */
        .feature-title {
          font-family: var(--font-display);
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--white);
          margin: 0 0 0.5rem;
          letter-spacing: -0.01em;
        }

        /* Card body — sans light */
        .feature-desc {
          font-family: var(--font-body);
          font-size: 0.88rem;
          font-weight: 400;
          color: rgba(23,35,25,0.7);
          line-height: 1.65;
          margin: 0;
        }

        /* ── Progress bar ────────────────────────────────────────── */
        .progress-container {
          position: absolute;
          bottom: 44px;
          left: 50%;
          transform: translateX(-50%);
          width: min(280px, 80vw);
          z-index: 4;
          text-align: center;
        }

        .progress-text {
          font-family: var(--font-body);
          font-size: 0.62rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          color: rgba(23,35,25,0.4);
          margin-bottom: 10px;
        }

        .progress-bar {
          width: 100%;
          height: 2px;
          background: rgba(122,158,126,0.2);
          border-radius: 2px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          width: 0%;
          background: linear-gradient(90deg, #7A9E7E 0%, #A8C5AA 50%, #7A9E7E 100%);
          background-size: 200% 100%;
          border-radius: 2px;
          transition: width 0.1s linear;
          animation: shimmer 2.5s linear infinite;
        }

        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ── Responsive ──────────────────────────────────────────── */
        @media (max-width: 968px) {
          .feature-cards { grid-template-columns: 1fr; gap: 1rem; }
          .step-connector { display: none; }
        }

        @media (max-width: 640px) {
          .progress-steps {
            display: grid;
            grid-template-columns: repeat(3,1fr);
            gap: 0.75rem;
          }
          .content-layer { padding: 1.5rem 1rem; }
          .step-number { width: 42px; height: 42px; font-size: 1rem; }
        }
      </style>

      <section class="scroll-video-section" aria-label="Precision in Motion">
        <div class="sticky-container">

          <!-- ══ SVG Background ══════════════════════════════════════ -->
          <svg class="svg-bg" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice"
               xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="curveGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stop-color="#A8C5AA" stop-opacity="0"/>
                <stop offset="30%"  stop-color="#A8C5AA" stop-opacity="0.22"/>
                <stop offset="70%"  stop-color="#94B58A" stop-opacity="0.18"/>
                <stop offset="100%" stop-color="#A8C5AA" stop-opacity="0"/>
              </linearGradient>
              <linearGradient id="curveGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stop-color="#7A9E7E" stop-opacity="0"/>
                <stop offset="40%"  stop-color="#7A9E7E" stop-opacity="0.15"/>
                <stop offset="60%"  stop-color="#A8C5AA" stop-opacity="0.12"/>
                <stop offset="100%" stop-color="#7A9E7E" stop-opacity="0"/>
              </linearGradient>
              <linearGradient id="hexGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%"   stop-color="#94B58A" stop-opacity="0.35"/>
                <stop offset="100%" stop-color="#7A9E7E" stop-opacity="0.08"/>
              </linearGradient>
              <filter id="hexGlow">
                <feGaussianBlur stdDeviation="2.5" result="blur"/>
                <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>

            <!-- Far hexagon layer (large, faint) -->
            <g class="hex-layer-far" opacity="0.22">
              <polygon points="160,80  220,45  280,80  280,150 220,185 160,150" fill="none" stroke="url(#hexGrad)" stroke-width="1.2"/>
              <polygon points="840,60  920,17  1000,60 1000,145 920,188 840,145" fill="none" stroke="url(#hexGrad)" stroke-width="1"/>
              <polygon points="1640,120 1710,80 1780,120 1780,200 1710,240 1640,200" fill="none" stroke="url(#hexGrad)" stroke-width="1.2"/>
              <polygon points="60,700  130,658 200,700 200,782 130,824 60,782" fill="none" stroke="url(#hexGrad)" stroke-width="1"/>
              <polygon points="1760,780 1830,738 1900,780 1900,862 1830,904 1760,862" fill="none" stroke="url(#hexGrad)" stroke-width="1.2"/>
              <polygon points="500,900 570,858 640,900 640,982 570,1024 500,982" fill="none" stroke="url(#hexGrad)" stroke-width="0.8"/>
              <polygon points="1200,920 1265,880 1330,920 1330,998 1265,1038 1200,998" fill="none" stroke="url(#hexGrad)" stroke-width="0.8"/>
            </g>

            <!-- Mid hexagon layer — medium scale, slightly more visible -->
            <g class="hex-layer-mid" opacity="0.38" filter="url(#hexGlow)">
              <polygon points="380,180 430,151 480,180 480,238 430,267 380,238" fill="none" stroke="#94B58A" stroke-width="1.5"/>
              <polygon points="1100,200 1155,168 1210,200 1210,265 1155,297 1100,265" fill="none" stroke="#94B58A" stroke-width="1.5"/>
              <polygon points="1500,380 1548,352 1596,380 1596,434 1548,462 1500,434" fill="none" stroke="#A8C5AA" stroke-width="1.2"/>
              <polygon points="250,550 300,520 350,550 350,608 300,638 250,608" fill="none" stroke="#94B58A" stroke-width="1.3"/>
              <polygon points="900,820 952,790 1004,820 1004,880 952,910 900,880" fill="none" stroke="#A8C5AA" stroke-width="1.2"/>
              <polygon points="1700,600 1748,572 1796,600 1796,656 1748,684 1700,656" fill="none" stroke="#94B58A" stroke-width="1"/>
              <polygon points="680,380 724,355 768,380 768,430 724,455 680,430" fill="none" stroke="#7A9E7E" stroke-width="1.5"/>
            </g>

            <!-- Near hexagon layer — smaller, accent-bright, animated independently -->
            <g class="hex-layer-near" opacity="0.55" filter="url(#hexGlow)">
              <polygon points="120,340 148,324 176,340 176,372 148,388 120,372" fill="rgba(148,181,138,0.06)" stroke="#94B58A" stroke-width="1.8"/>
              <polygon points="1800,300 1828,284 1856,300 1856,332 1828,348 1800,332" fill="rgba(148,181,138,0.08)" stroke="#A8C5AA" stroke-width="1.8"/>
              <polygon points="960,140 990,123 1020,140 1020,175 990,192 960,175"  fill="rgba(148,181,138,0.06)" stroke="#94B58A" stroke-width="2"/>
              <polygon points="440,720 468,704 496,720 496,752 468,768 440,752"    fill="rgba(148,181,138,0.07)" stroke="#A8C5AA" stroke-width="1.6"/>
              <polygon points="1380,760 1408,744 1436,760 1436,792 1408,808 1380,792" fill="rgba(148,181,138,0.06)" stroke="#94B58A" stroke-width="1.6"/>
              <polygon points="740,560 764,547 788,560 788,587 764,600 740,587"    fill="rgba(148,181,138,0.08)" stroke="#94B58A" stroke-width="2"/>
            </g>

            <!-- Organic curves layer -->
            <g class="curves-layer">
              <!-- Main sweeping curves -->
              <path d="M-80,320 C380,140 900,520 1440,280 S1780,180 2000,240"
                    fill="none" stroke="url(#curveGrad1)" stroke-width="1.5"/>
              <path d="M-80,500 C300,620 820,320 1300,480 S1680,560 2000,440"
                    fill="none" stroke="url(#curveGrad2)" stroke-width="1"/>
              <path d="M-80,700 C440,820 960,580 1500,720 S1780,800 2000,740"
                    fill="none" stroke="url(#curveGrad1)" stroke-width="1.2"/>
              <path d="M-80,160 C260,280 700,80  1160,220 S1620,320 2000,200"
                    fill="none" stroke="url(#curveGrad2)" stroke-width="0.8"/>
              <path d="M-80,880 C500,960 1000,820 1520,900 S1800,960 2000,920"
                    fill="none" stroke="url(#curveGrad1)" stroke-width="0.8"/>

              <!-- Diagonal accent lines -->
              <line x1="0"    y1="0"    x2="400"  y2="600"  stroke="rgba(148,181,138,0.06)" stroke-width="1"/>
              <line x1="1920" y1="0"    x2="1520" y2="600"  stroke="rgba(148,181,138,0.06)" stroke-width="1"/>
              <line x1="0"    y1="1080" x2="400"  y2="480"  stroke="rgba(148,181,138,0.05)" stroke-width="1"/>
              <line x1="1920" y1="1080" x2="1520" y2="480"  stroke="rgba(148,181,138,0.05)" stroke-width="1"/>
            </g>
          </svg>

          <!-- Rotating gradient mesh (matches eco-product-story) -->
          <div class="grad-mesh"></div>

          <!-- Edge vignette -->
          <div class="vignette"></div>

          <!-- ══ Content ══════════════════════════════════════════════ -->
          <div class="content-layer">

            <div class="section-header">
              <span class="section-label">
                <span class="label-dot"></span>
                Cleaning System Series
              </span>
              <h2 class="section-title">Precision in <em>Motion</em></h2>
              <p class="section-subtitle">
                From concept to commissioning, every step is engineered for excellence
                and validated for compliance.
              </p>
            </div>

            <!-- Steps with connectors -->
            <div class="progress-steps">
              <div class="step active" data-step="1">
                <div class="step-number">1</div>
                <div class="step-label">Design</div>
              </div>
              <div class="step-connector"></div>
              <div class="step" data-step="2">
                <div class="step-number">2</div>
                <div class="step-label">Fabrication</div>
              </div>
              <div class="step-connector"></div>
              <div class="step" data-step="3">
                <div class="step-number">3</div>
                <div class="step-label">Automation</div>
              </div>
              <div class="step-connector"></div>
              <div class="step" data-step="4">
                <div class="step-number">4</div>
                <div class="step-label">Validation</div>
              </div>
              <div class="step-connector"></div>
              <div class="step" data-step="5">
                <div class="step-number">5</div>
                <div class="step-label">Commissioning</div>
              </div>
            </div>

            <!-- Feature Cards -->
            <div class="feature-cards">
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                </div>
                <h3 class="feature-title">Custom Design</h3>
                <p class="feature-desc">Tailored solutions engineered to your specific process requirements and space constraints.</p>
              </div>
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                  </svg>
                </div>
                <h3 class="feature-title">SS316 Fabrication</h3>
                <p class="feature-desc">Premium stainless steel construction with precision welding and immaculate surface finishing.</p>
              </div>
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </div>
                <h3 class="feature-title">Smart Automation</h3>
                <p class="feature-desc">PLC-controlled systems with HMI interfaces and full remote monitoring capabilities.</p>
              </div>
            </div>
          </div>

          <!-- Progress bar -->
          <div class="progress-container">
            <div class="progress-text">Scroll to explore</div>
            <div class="progress-bar">
              <div class="progress-fill" id="progressFill"></div>
            </div>
          </div>

        </div>
      </section>
    `;
  }

  initScrollVideo() {
    const progressFill = this.shadowRoot.getElementById('progressFill');
    const steps = this.shadowRoot.querySelectorAll('.step[data-step]');
    const featureCards = this.shadowRoot.querySelectorAll('.feature-card');
    const section = this.shadowRoot.querySelector('.scroll-video-section');
    const hexLayers = this.shadowRoot.querySelectorAll('.hex-layer-far, .hex-layer-mid, .hex-layer-near');
    const curvesLayer = this.shadowRoot.querySelector('.curves-layer');

    if (!section) return;

    // Main scrub trigger
    this.scrollTrigger = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.9,
      onUpdate: (self) => {
        const p = self.progress;

        // Parallax-shift SVG layers at different speeds
        if (hexLayers[0]) hexLayers[0].style.transform = `translateX(${p * 40}px)`;
        if (hexLayers[1]) hexLayers[1].style.transform = `translateX(${-p * 28}px) translateY(${p * 14}px)`;
        if (hexLayers[2]) hexLayers[2].style.transform = `translateX(${p * 18}px) translateY(${-p * 22}px)`;
        if (curvesLayer) curvesLayer.style.transform = `translateX(${p * 55}px)`;

        // Progress bar
        if (progressFill) progressFill.style.width = `${p * 100}%`;

        // Step activation
        const activeIdx = Math.min(Math.floor(p * 5), 4);
        steps.forEach((step, i) => {
          step.classList.toggle('active', i <= activeIdx);
        });

        // Staggered card reveal
        featureCards.forEach((card, i) => {
          if (p > 0.22 + i * 0.08 && !card.classList.contains('visible')) {
            gsap.to(card, {
              opacity: 1, y: 0,
              duration: 0.65,
              delay: i * 0.12,
              ease: 'power2.out',
              onStart: () => card.classList.add('visible')
            });
          }
        });
      }
    });

    // Pin
    this.pinTrigger = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      pin: this.shadowRoot.querySelector('.sticky-container'),
      pinSpacing: false
    });

    // Step click — scroll to that scene
    steps.forEach((step, i) => {
      step.addEventListener('click', () => {
        const targetY = section.offsetTop + (section.offsetHeight * (i / 4)) - window.innerHeight;
        gsap.to(window, {
          duration: 1.2,
          scrollTo: { y: targetY, autoKill: false },
          ease: 'power2.inOut'
        });
      });
    });
  }
}

customElements.define('eco-scroll-video', EcoScrollVideo);